import { Plus, ArrowRight, ArrowLeft, Images } from "@phosphor-icons/react/dist/ssr";
import { ArrayFieldTemplateProps } from "@rjsf/utils";
import { t } from "i18next";
import useUpdateSectionAction from "../../actions/updateSection/useUpdateSectionAction";
import { Button, Spinner } from "react-bootstrap";
import React, { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { getApi } from "@beeldit/core/helpers/api";
import { showErrorMessage } from "@beeldit/core/helpers/messagesHelper";
import httpErrorMessage from "@beeldit/core/errors/httpErrorMessage";

const uploadFile = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const config = {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  };

  const host = process.env.REACT_APP_API_URL || "";
  try {
    const response = await getApi().post(
      `${host}/files/upload`,
      formData,
      config
    );
    response.data.add = true;
    return response.data;
  } catch (error: any) {
    showErrorMessage(httpErrorMessage(error));
    return null;
  }
};

/**
 * Convierte un idSchema.$id de rjsf (ej: "root_sections_0_content_elements")
 * en la ruta de propiedades para acceder al formData.
 */
function idToPath(id: string): (string | number)[] {
  const parts = id.replace(/^root_?/, "").split("_");
  return parts
    .filter((p) => p !== "")
    .map((p) => (isNaN(Number(p)) ? p : Number(p)));
}

/**
 * Obtiene un valor anidado siguiendo un path de claves.
 */
function getNestedValue(obj: any, path: (string | number)[]): any {
  return path.reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
}

/**
 * Devuelve una copia profunda del objeto con el valor establecido en el path indicado.
 */
function setNestedValue(obj: any, path: (string | number)[], value: any): any {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  clone[head] = setNestedValue(clone[head], rest, value);
  return clone;
}

function GalleryArrayTemplate(
  props: ArrayFieldTemplateProps,
  className: string,
  id: string
) {
  const { actionTemplate } = useUpdateSectionAction();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [showDropzone, setShowDropzone] = useState(false);
  const [uploading, setUploading] = useState(false);

  const formContext = props.formContext as
    | { setElement: (el: any) => void; element: any }
    | undefined;

  const handleDragStart = (index: number) => (e: React.DragEvent) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = "0.5";
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = "1";
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (targetIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();

    if (draggedIndex !== null && draggedIndex !== targetIndex) {
      const element = props.items[draggedIndex];
      if (element && element.onReorderClick) {
        element.onReorderClick(draggedIndex, targetIndex)();
      }
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const onDropFiles = useCallback(
    async (acceptedFiles: File[]) => {
      if (!formContext || acceptedFiles.length === 0) return;

      setUploading(true);
      try {
        const uploadResults = await Promise.all(
          acceptedFiles.map((file) => uploadFile(file))
        );
        const successfulUploads = uploadResults.filter(
          (result) => result !== null
        );

        if (successfulUploads.length === 0) {
          setUploading(false);
          return;
        }

        // Construir nuevos items de galería con la imagen pre-rellenada
        const newItems = successfulUploads.map((fileData) => ({
          img: [fileData],
          alt: "",
          main: false,
        }));

        // Obtener el array actual y añadir los nuevos items
        const path = idToPath(id);
        const currentArray = getNestedValue(formContext.element, path) || [];
        const updatedArray = [...currentArray, ...newItems];
        const updatedElement = setNestedValue(
          formContext.element,
          path,
          updatedArray
        );

        formContext.setElement(updatedElement);
        setShowDropzone(false);
      } catch (error) {
        console.error("Error uploading files:", error);
      } finally {
        setUploading(false);
      }
    },
    [formContext, id]
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop: onDropFiles,
    accept: { "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp", ".svg"] },
    multiple: true,
    disabled: uploading,
  });

  const dropzoneStyle: React.CSSProperties = useMemo(
    () => ({
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px 20px",
      borderWidth: 2,
      borderRadius: 8,
      borderColor: isDragAccept
        ? "#00e676"
        : isDragReject
        ? "#ff1744"
        : isDragActive
        ? "#2196f3"
        : "#cccccc",
      borderStyle: "dashed",
      backgroundColor: isDragActive ? "rgba(33, 150, 243, 0.05)" : "#fafafa",
      color: "#888",
      outline: "none",
      transition: "border .24s ease-in-out, background-color .24s ease-in-out",
      cursor: uploading ? "wait" : "pointer",
      minHeight: "160px",
    }),
    [isDragActive, isDragAccept, isDragReject, uploading]
  );

  return (
    <div className={'cms-section '+className}>
      {id !== "root_sections" ? <h2 className="cms-section-title">{props.title} {actionTemplate}</h2> : null}
        <div className="gallery-grid">
          {props.items.map((element, index) => {
            const isDragging = draggedIndex === index;
            const isDragOver = dragOverIndex === index;
            return (
                <div
                  className={`gallery-item ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                  key={element.key}
                  draggable={id !== "root_sections"}
                  onDragStart={handleDragStart(index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver(index)}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop(index)}
                >
                  {id !== "root_sections" ? (
                    <div className="gallery-item-header">
                      <h3 className="gallery-item-title sr-only">
                        {t("image")} {index + 1}{" "}
                      </h3>
                      {props.canAdd && (
                        <Button className="gallery-item-remove" variant="light" onClick={element.onDropIndexClick(index)}>
                          <span className="sr-only">{t("delete")}</span>
                        </Button>
                      )}
                    </div>
                  ) : null}

                  <div className="flex-grow-1">{element.children}</div>

                  {/* Botones de ordenación (mantener como alternativa de accesibilidad) */}
                  <div className="gallery-item-sort">
                    {element.hasMoveUp && (
                      <Button
                        variant="light"
                        className="btn-icon"
                        onClick={element.onReorderClick(index, index - 1)}
                        title={t("move_up")}
                      >
                        <ArrowLeft size={16} />
                        <span className="sr-only">{t("move_up")}</span>
                      </Button>
                    )}
                    {element.hasMoveDown && (
                      <Button
                        variant="light"
                        className="btn-icon"
                        onClick={element.onReorderClick(index, index + 1)}
                        title={t("move_down")}
                      >
                        <ArrowRight size={16} />
                        <span className="sr-only">{t("move_down")}</span>
                      </Button>
                    )}
                  </div>
                </div>
            );
          })}
        </div>

        {/* Dropzone para múltiples imágenes */}
        {showDropzone && props.canAdd && (
          <div className="gallery-dropzone-container mt-3">
            <div {...getRootProps({ style: dropzoneStyle })}>
              <input {...getInputProps()} />
              {uploading ? (
                <div className="text-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <p className="mb-0 mt-2">{t("uploading_images")}</p>
                </div>
              ) : isDragActive ? (
                <p className="mb-0">{t("drop_images_here")}</p>
              ) : (
                <div className="text-center">
                  <Images size={48} className="mb-2" />
                  <p className="mb-0">{t("drag_drop_images")}</p>
                  <small className="text-muted">{t("or_click_to_select")}</small>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="text-center mt-4">
          {props.canAdd && (
            <Button
              variant="outline-primary"
              onClick={() => setShowDropzone(!showDropzone)}
            >
              <Plus size={24} />
              <span>{t("add_images")}</span>
            </Button>
          )}
        </div>
    </div>
  );
}

export default GalleryArrayTemplate;
